import { Check, Close } from '@mui/icons-material'
import { Grid, Tooltip, Typography } from '@mui/material'
import { DataGridPremium, GridActionsCellItem, GridAlignment, GridColumnMenu, GridColumnMenuProps, GridEventListener, useGridApiRef } from '@mui/x-data-grid-premium'
import { ProjectInvite } from '../../../../libs/api/projects/ProjectModel'
import { respondProjectInvite } from '../../../../libs/api/users/UserAPI'
import { useNotificationContext } from '../../../../context/NotificationContext'
import ConfirmationDialog from '../../../../components/ConfirmationDialog'
import { useState } from 'react'

const HEADER_ROW_BACKGROUND_GRADIENT = 'linear-gradient(rgba(240, 240, 255, 0.55), rgba(240, 240, 255, 0.3))'

interface InviteResponseAcceptance {
    projectId: string
    projectName: string
    hasAccepted: boolean
}

interface ProjectInvitesGridProps {
    invites: ProjectInvite[]
    onRefreshProjectView: () => void
}

export function ProjectInvitesGrid(props: ProjectInvitesGridProps) {
    const { invites, onRefreshProjectView } = props
    const [dialogOpen, setDialogOpen] = useState<boolean>(false)
    const [responseInvite, setResponseInvite] = useState<InviteResponseAcceptance>()

    const apiRef = useGridApiRef()
    const openNotification = useNotificationContext().openNotification

    const handleInviteResponseAction = (projectId: string, hasAccepted: boolean) => {
        const { projectName } = invites.find((invite) => invite.projectId === projectId)

        if (hasAccepted) {
            handleInviteResponse(projectId, hasAccepted)
        } else {
            setResponseInvite({ projectId, projectName, hasAccepted })
            setDialogOpen(true)
        }
    }

    const handleInviteResponse = async (projectId?: string, hasAccepted?: boolean) => {
        const { succeeded, data, errors } = await respondProjectInvite(projectId ? projectId : responseInvite.projectId, hasAccepted ? hasAccepted : responseInvite.hasAccepted)
        if (succeeded) {
            openNotification(data.message, 'success')
            onRefreshProjectView()
        } else {
            openNotification(errors.message, errors.severity)
        }
    }

    const getActions = getActionsArray(handleInviteResponseAction)
    const builtColumns = buildColumns(getActions)

    function CustomColumnMenu(props: GridColumnMenuProps) {
        return (
            <GridColumnMenu
                {...props}
                slots={{
                    columnMenuGroupingItem: null,
                    columnMenuPinningItem: null,
                    columnMenuColumnsItem: null,
                }}
            />
        )
    }

    const onRowClick: GridEventListener<'rowClick'> = (params) => {
        if ((params.id as string).startsWith('auto-generated')) {
            const rowNode = apiRef.current.getRowNode(params.id)
            if (rowNode && rowNode.type === 'group') {
                apiRef.current.setRowChildrenExpansion(params.id, !rowNode.childrenExpanded)
            }
        }
    }

    return (
        <>
            <DataGridPremium
                apiRef={apiRef}
                getRowId={(r) => r.projectId}
                autoHeight
                rows={invites}
                columns={builtColumns}
                initialState={initialState}
                slots={{
                    columnHeaders: () => null,
                    columnMenu: CustomColumnMenu,
                }}
                groupingColDef={{
                    availableAggregationFunctions: [],
                    headerName: 'Pending Invitations',
                    minWidth: 250,
                }}
                isGroupExpandedByDefault={() => {
                    return true
                }}
                onRowClick={onRowClick}
                disableRowSelectionOnClick
                hideFooter
                sx={{
                    '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus': {
                        outline: 'none',
                    },
                    '.MuiDataGrid-cell:focus-within': {
                        outline: 'none',
                    },
                    '.MuiDataGrid-row:hover': {
                        cursor: 'pointer',
                    },
                    '.MuiDataGrid-groupingCriteriaCell': {
                        fontWeight: 'bold',
                    },
                    '.MuiDataGrid-row:has([data-testid="ExpandMoreIcon"])': {
                        backgroundImage: HEADER_ROW_BACKGROUND_GRADIENT,
                        cursor: 'pointer',
                        borderRadius: '5px 5px 0 0',
                    },
                    border: '2px solid rgba(34, 34, 58, 0.3)',
                    marginTop: '15px',
                    marginBottom: '20px',
                    width: '100%',
                }}
            />
            <ConfirmationDialog
                openDialog={dialogOpen}
                title={'Are you sure?'}
                description={`${responseInvite?.projectName} is waiting for your response. Declining the invitation means you will not have access to this project.`}
                confirmText={'Decline'}
                cancelText={'Cancel'}
                onConfirm={() => {
                    setDialogOpen(false)
                    handleInviteResponse()
                    setResponseInvite(null)
                }}
                onCancel={() => {
                    setDialogOpen(false)
                    setResponseInvite(null)
                }}
            />
        </>
    )
}

/**
 * Grid columns setup
 * @param getActionsArray Callback to obtain the array of actions for invite response
 * @returns The rendering component array for the Grid
 */
const buildColumns = (getActionsArray: (params: any) => React.JSX.Element[]) => {
    return [
        {
            field: 'invites',
            headerName: 'Pending Invitations',
        },
        {
            field: 'projectId',
            headerName: 'Project Id',
        },
        {
            field: 'projectName',
            headerName: 'Project Name',
            flex: 3,
            cellClassName: 'project-name',
        },
        {
            field: 'isAdmin',
            headerName: 'User Role',
            flex: 1,
            minWidth: 100,
            renderCell: (params) => userRoleInProject(params),
        },
        { field: 'actions', type: 'actions', flex: 1, minWidth: 170, align: 'right' as GridAlignment, getActions: getActionsArray },
    ]
}

const userRoleInProject = (params: any) => {
    if (params.rowNode.type === 'group') {
        return null
    }

    const isAdmin = params.value
    return isAdmin ? 'Admin' : 'Contributor'
}

/**
 * Builds an array of action components for a invite response action
 * @param onInviteResponse Callback to respond to an invite
 * @returns The array of action components for the project invites
 */
function getActionsArray(onInviteResponse?: (projectId: string, hasAccepted: boolean) => void) {
    const actionsArray = (params: any) => {
        if (params.id.startsWith('auto-generated-row-invites')) {
            return []
        }

        return [
            <GridActionsCellItem
                key="decline"
                icon={
                    <Tooltip title="Decline project invite">
                        <Grid sx={{ display: 'flex', flexDirection: 'row', color: 'rgba(211, 47, 47, 1)' }}>
                            <Close fontSize="small" />
                            <Typography variant="body2">Decline</Typography>
                        </Grid>
                    </Tooltip>
                }
                onClick={() => {
                    onInviteResponse(params.id, false)
                }}
                label="Decline"
            />,
            <GridActionsCellItem
                key="accept"
                icon={
                    <Tooltip title="Accept project invite">
                        <Grid sx={{ display: 'flex', flexDirection: 'row', color: 'rgba(23, 98, 39, 1)' }}>
                            <Check fontSize="small" />
                            <Typography variant="body2">Accept</Typography>
                        </Grid>
                    </Tooltip>
                }
                onClick={() => {
                    onInviteResponse(params.id, true)
                }}
                label="Accept"
            />,
        ]
    }

    return actionsArray
}

/**
 * The datagrid initial state
 */
const initialState = {
    pagination: {
        paginationModel: { pageSize: 30, page: 0 },
    },
    rowGrouping: {
        model: ['invites'],
    },
    columns: {
        columnVisibilityModel: {
            invites: false,
            projectId: false,
        },
    },
}
